import { useEffect, useRef, useState } from "react";
import LittleRockHeroSection from "./components/HeroSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const navItems = [
  { label: "Processing", href: "#processing" },
  { label: "Services", href: "#services" },
  { label: "Local Area", href: "#local-area" },
  { label: "Timing", href: "#timing" },
  { label: "Destinations", href: "#destinations" },
  { label: "Facilities", href: "#facilities" },
  { label: "Compare", href: "#compare" },
  { label: "FAQ", href: "#faq" },
  { label: "Resources", href: "#resources" },
];

const faqs = [
  { q: "How fast can I get a passport in Little Rock?", a: "Our emergency service delivers your passport in as little as 24 hours. Standard expedited processing takes 3–5 business days, and our budget-friendly option takes 7–10 business days. We recommend starting at least 2–3 weeks before your departure date to avoid rush fees." },
  { q: "Where is the nearest passport acceptance facility in Little Rock?", a: "The main facility is the Little Rock Main Post Office at 600 E Capitol Ave. There are also facilities at the John L. McClellan Station and the Pulaski County Clerk's Office. We guide you to the best location with the shortest wait times." },
  { q: "Do I need an appointment for a passport photo in Little Rock?", a: "No appointment needed for our photo guidelines — you can take a compliant photo at home or at any CVS, Walgreens, or UPS Store in Little Rock. We provide exact specifications so your photo won't be rejected." },
  { q: "Can I get a same-day passport in Little Rock?", a: "Same-day processing is available through our emergency service for travelers with proof of immediate departure (within 14 days). Contact us at 877-253-0084 for same-day eligibility." },
  { q: "What documents do I need for a new passport in Little Rock?", a: "You need proof of U.S. citizenship (birth certificate or naturalization certificate), a government-issued photo ID, a compliant passport photo, and the completed DS-11 form. We provide a complete checklist and review everything before submission." },
  { q: "How much does expedited passport service cost in Little Rock?", a: "Government fees are $130–$190 depending on passport type. Our service fees start at $99 for standard expedited processing. Emergency 24-hour service is available at a higher tier. We guarantee the best price — find a lower rate and we'll match it." },
  { q: "Is Fast Passport Center a real office in Little Rock?", a: "We do not have a physical office in Little Rock. We operate as a registered courier service — you complete your application at a local acceptance facility, and we handle the expedited processing with the U.S. Department of State." },
  { q: "Can I renew my passport by mail from Little Rock?", a: "Yes, if your most recent passport was issued within the last 15 years when you were 16 or older, you can use Form DS-82 for renewal by mail. We can expedite this process with our courier service." },
];

const services = [
  { icon: "ri-passport-line", title: "New Passport", desc: "First-time application assistance for U.S. citizens of all ages." },
  { icon: "ri-refresh-line", title: "Passport Renewal", desc: "Fast renewal for expired or near-expired passports." },
  { icon: "ri-alert-line", title: "Lost/Stolen", desc: "Replacement service with DS-64 form guidance." },
  { icon: "ri-user-smile-line", title: "Child Passport", desc: "Minors under 16 with parental consent support." },
  { icon: "ri-time-line", title: "Emergency", desc: "24-hour processing for immediate departures." },
  { icon: "ri-edit-line", title: "Name Change", desc: "Post-marriage, divorce, or legal name updates." },
];

const destinations = [
  { name: "Cancun", country: "Mexico", flag: "🇲🇽", desc: "Most popular spring break and family destination from Little Rock. Direct flights available via Dallas." },
  { name: "London", country: "UK", flag: "🇬🇧", desc: "Business and leisure travel hub. Popular with UALR study abroad students." },
  { name: "Barcelona", country: "Spain", flag: "🇪🇸", desc: "Top European destination for Arkansas travelers. Rich culture and Mediterranean beaches." },
  { name: "Tokyo", country: "Japan", flag: "🇯🇵", desc: "Growing business destination for Little Rock corporate travelers." },
  { name: "Cruise Ports", country: "Various", flag: "🚢", desc: "New Orleans and Galveston cruise departures — most popular for Little Rock families." },
  { name: "Sydney", country: "Australia", flag: "🇦🇺", desc: "Adventure and educational travel destination for long-term trips." },
];

const facilities = [
  { name: "Little Rock Main Post Office", address: "600 E Capitol Ave, Little Rock, AR 72201", phone: "(501) 324-5922", hours: "Mon–Fri 9:00 AM – 4:00 PM", wait: "30–60 min", appt: "Walk-in" },
  { name: "John L. McClellan Station", address: ".PO Box 1920, Little Rock, AR 72203", phone: "(501) 324-5922", hours: "Mon–Fri 9:00 AM – 3:00 PM", wait: "20–45 min", appt: "Walk-in" },
  { name: "Pulaski County Clerk's Office", address: "401 W Markham St, Little Rock, AR 72201", phone: "(501) 340-8383", hours: "Mon–Fri 8:00 AM – 4:30 PM", wait: "15–30 min", appt: "Walk-in" },
  { name: "North Little Rock Post Office", address: "300 Main St, North Little Rock, AR 72114", phone: "(501) 758-2390", hours: "Mon–Fri 9:00 AM – 4:00 PM", wait: "20–40 min", appt: "Walk-in" },
];

const testimonials = [
  { name: "Jennifer M.", city: "Hillcrest, Little Rock", stars: 5, text: "My passport expired 3 weeks before our family trip to Cancun. Fast Passport Center got it renewed in 4 days. Saved our vacation!" },
  { name: "Robert K.", city: "West Little Rock", stars: 5, text: "State agency official here — had a diplomatic trip to London with 10 days notice. They handled everything flawlessly." },
  { name: "Sarah T.", city: "North Little Rock", stars: 5, text: "Used the emergency service for my daughter's study abroad. Passport in hand in 48 hours. Incredible service." },
];

const comparison = [
  { feature: "Processing Speed", us: "As fast as 24 hours", them: "10–13 weeks standard" },
  { feature: "Office Visit Required", us: "No — mail or drop-off", them: "Yes — in-person required" },
  { feature: "Photo Review", us: "Included — we verify compliance", them: "Self-service — rejections common" },
  { feature: "Application Review", us: "Expert review before submission", them: "No review — errors cause delays" },
  { feature: "Tracking", us: "Real-time status updates", them: "Limited status info" },
  { feature: "Customer Support", us: "Phone + email, 7 days/week", them: "Phone only, limited hours" },
  { feature: "Price Guarantee", us: "Best price guarantee", them: "No price matching" },
  { feature: "Acceptance Facility Help", us: "We find the shortest wait times", them: "Self-schedule at any facility" },
  { feature: "Refund Policy", us: "Satisfaction guarantee", them: "No refunds after submission" },
];

const mistakes = [
  { title: "Waiting Too Long", desc: "Little Rock residents often wait until 2 weeks before travel to start. Start at least 3–4 weeks early to avoid rush fees and stress." },
  { title: "Wrong Photo Size", desc: "CVS and Walgreens photos are often rejected. We provide exact specs and review your photo before submission." },
  { title: "Missing Documents", desc: "Birth certificates without a raised seal are commonly rejected. We verify all your documents before you visit the acceptance facility." },
  { title: "Booking Travel Before Passport", desc: "Never book non-refundable flights before verifying your passport is valid. We see this mistake weekly from Little Rock travelers." },
  { title: "Using the Wrong Form", desc: "DS-11 vs DS-82 confusion causes major delays. We tell you exactly which form to use based on your situation." },
];

export default function LittleRockPage() {
  useEffect(() => {
    document.title = "Emergency Passport Little Rock, AR — Expedited Same-Day Service | Fast Passport Center";
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };
    const pageUrl = "https://fastpassportcenter.com/little-rock/";
    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Little Rock, AR — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Arkansas state officials, UALR students, and families.");
    setMeta("geo.placename", "Little Rock, Arkansas");
    setMeta("geo.region", "US-AR");
    setMeta("geo.position", "34.7465;-92.2896");
    setMeta("ICBM", "34.7465, -92.2896");
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
            { "@type": "ListItem", position: 2, name: "Arkansas", item: "https://fastpassportcenter.com/arkansas/" },
            { "@type": "ListItem", position: 3, name: "Little Rock Passport Services", item: pageUrl },
          ],
        },
        {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
          description: "Fast Passport Center does not have a physical office in Little Rock — we serve this area remotely. Emergency and expedited passport services as fast as 24 hours.",
          url: pageUrl,
          telephone: "+1-877-253-0084",
          areaServed: { "@type": "City", name: "Little Rock", containedInPlace: { "@type": "State", name: "Arkansas" } },
          priceRange: "$$$",
          openingHours: ["Mo-Fr 09:00-18:00"],
          aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "1200" },
        },
        {
          "@type": "FAQPage",
          mainEntity: faqs.map((faq) => ({ "@type": "Question", name: faq.q, acceptedAnswer: { "@type": "Answer", text: faq.a } })),
        },
      ],
    });
    document.head.appendChild(script);
    return () => {
      document.getElementById("structured-data")?.remove();
      document.querySelector('link[rel="canonical"]')?.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <LittleRockHeroSection />
      <StickyNav />
      <ProcessingSection />
      <ServicesSection />
      <LocalAreaSection />
      <TravelTimingSection />
      <DestinationsSection />
      <TestimonialsSection />
      <AcceptanceFacilitiesSection />
      <ComparisonSection />
      <MistakesSection />
      <HowItWorksSection />
      <FAQSection />
      <HelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/little-rock" state="AR" />
      <FinalCTA />
    </div>
  );
}

function StickyNav() {
  const [active, setActive] = useState("");
  useEffect(() => {
    const handle = () => {
      const sections = navItems.map((n) => document.querySelector(n.href));
      const scroll = window.scrollY + 120;
      for (let i = sections.length - 1; i >= 0; i--) {
        const s = sections[i];
        if (s && s.getBoundingClientRect().top + window.scrollY <= scroll) {
          setActive(navItems[i].href);
          return;
        }
      }
      setActive("");
    };
    window.addEventListener("scroll", handle);
    return () => window.removeEventListener("scroll", handle);
  }, []);
  return (
    <div className="sticky top-[70px] z-40 bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center gap-1 overflow-x-auto py-2 scrollbar-hide">
          {navItems.map((item) => (
            <a key={item.href} href={item.href} className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${active === item.href ? "bg-[#1A365D] text-white" : "text-gray-600 hover:bg-gray-100"}`}>{item.label}</a>
          ))}
        </div>
      </div>
    </div>
  );
}

function ProcessingSection() {
  return (
    <section id="processing" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Processing Options</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How Fast Can You Get a Passport in Little Rock?</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base">Three service tiers designed for every timeline and budget.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { title: "Emergency", time: "24–48 Hours", price: "From $299", badge: "Fastest", desc: "For travelers with immediate departure needs. We hand-carry your application to the State Department.", features: ["24-hour turnaround available", "Hand-carry courier service", "Real-time tracking", "Phone support 7 days/week"] },
            { title: "Expedited", time: "3–5 Business Days", price: "From $149", badge: "Most Popular", desc: "The sweet spot for most travelers. Fast enough for urgent trips, affordable enough for budget-conscious families.", features: ["3–5 day guaranteed delivery", "Application review included", "Photo compliance check", "Email + phone support"] },
            { title: "Standard", time: "7–10 Business Days", price: "From $99", badge: "Best Value", desc: "For travelers with a few weeks before departure. Still faster than the government standard 10–13 weeks.", features: ["7–10 day processing", "Complete application guidance", "Document checklist", "Email support"] },
          ].map((plan, i) => (
            <div key={i} className={`bg-white border rounded-xl p-7 flex flex-col ${plan.badge === "Most Popular" ? "border-[#F6C344] ring-1 ring-[#F6C344]" : "border-gray-200"}`}>
              <div className="flex items-center justify-between mb-4">
                <span className={`text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${plan.badge === "Fastest" ? "bg-red-100 text-red-700" : plan.badge === "Most Popular" ? "bg-[#F6C344]/20 text-[#1A365D]" : "bg-gray-100 text-gray-700"}`}>{plan.badge}</span>
                <span className="text-[#1A365D] font-bold text-lg">{plan.price}</span>
              </div>
              <h3 className="font-bold text-[#1A365D] text-xl mb-1">{plan.title}</h3>
              <p className="text-[#F6C344] font-semibold text-sm mb-3">{plan.time}</p>
              <p className="text-gray-500 text-sm leading-relaxed mb-5 flex-1">{plan.desc}</p>
              <ul className="space-y-2 mb-6">
                {plan.features.map((f, j) => (
                  <li key={j} className="flex items-center gap-2 text-sm text-gray-600"><i className="ri-check-line text-[#F6C344] flex-shrink-0"></i>{f}</li>
                ))}
              </ul>
              <a href="https://fastpassportcenter.com/checkout/" className="block text-center bg-[#1A365D] hover:bg-[#0f2341] text-white font-semibold text-sm py-3 rounded-lg transition-all whitespace-nowrap">Get Started</a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServicesSection() {
  return (
    <section id="services" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Our Services</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services for Little Rock Travelers</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base">Every passport situation covered — from first-time applications to emergency replacements.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-6 hover:border-[#F6C344] transition-all">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-4">
                <i className={`${s.icon} text-[#1A365D] text-xl`}></i>
              </div>
              <h3 className="font-bold text-[#1A365D] text-lg mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LocalAreaSection() {
  return (
    <section id="local-area" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services in Little Rock, Arkansas</h2>
        </div>
        <div className="max-w-3xl mx-auto text-gray-600 text-base leading-relaxed space-y-5">
          <p>Little Rock, the capital and most populous city of Arkansas, serves as the political, economic, and cultural hub of the state. Nestled along the Arkansas River, this vibrant city of over 200,000 residents is home to state government agencies, the University of Arkansas at Little Rock, major hospitals, and a growing corporate presence. With the Clinton National Airport (LIT) offering connections to international hubs in Dallas, Atlanta, and Chicago, Little Rock travelers are well-positioned for both domestic and international journeys.</p>
          <p>The city's diverse neighborhoods — from the historic charm of Hillcrest and the Heights to the bustling River Market district and the expanding suburbs of West Little Rock and North Little Rock — each have their own unique travel needs. State officials and lobbyists frequently need expedited passports for last-minute diplomatic or business travel. UALR students and faculty require passports for study abroad programs, international research collaborations, and academic conferences. Medical professionals at UAMS and Baptist Health often need travel documents for international medical missions, conferences, and continuing education.</p>
          <p>Little Rock families are increasingly traveling abroad — spring break trips to Cancun and the Caribbean, summer vacations to Europe, and holiday cruises from New Orleans or Galveston. The local passport acceptance facilities, while functional, often have wait times of 30–60 minutes and limited appointment availability. That's where Fast Passport Center comes in. We help Little Rock residents navigate the entire passport process — from selecting the right form to finding the acceptance facility with the shortest wait time to hand-delivering your application for expedited processing. As a registered courier with the U.S. Department of State, we offer processing speeds that simply aren't available through standard channels.</p>
          <p>Whether you're a downtown professional with a sudden business trip to London, a teacher in the Pulaski County School District planning a European summer, or a family in Chenal Valley preparing for a Mexican beach vacation, we understand the urgency and anxiety that comes with passport deadlines. Our local expertise means we know which Little Rock facilities have the shortest lines, which forms are required for your specific situation, and how to avoid the common mistakes that delay applications. With over 20 years of experience and an A+ BBB rating, we've helped thousands of Arkansas travelers get their passports on time — every time.</p>
        </div>
      </div>
    </section>
  );
}

function TravelTimingSection() {
  return (
    <section id="timing" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Travel Timing</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">When Little Rock Travelers Need Passports</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { month: "January–February", title: "Winter Escape", desc: "Post-holiday Caribbean and Mexico cruises. Spring break planning begins." },
            { month: "March–April", title: "Spring Break Peak", desc: "Cancun, Jamaica, and Dominican Republic are top destinations. Rush orders spike." },
            { month: "May–June", title: "Summer Prep", desc: "European vacations and study abroad programs. Families book 3–4 months out." },
            { month: "July–August", title: "Summer Travel", desc: "Peak international travel season. Last-minute passport requests are common." },
            { month: "September–October", title: "Off-Season Deals", desc: "Fewer passport requests but ideal for renewal before holiday travel." },
            { month: "November–December", title: "Holiday Rush", desc: "Winter cruises and holiday trips to Europe. Emergency orders peak." },
          ].map((t, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-6">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-wider mb-2">{t.month}</p>
              <h3 className="font-bold text-[#1A365D] text-lg mb-2">{t.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{t.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DestinationsSection() {
  return (
    <section id="destinations" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Popular Destinations</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Where Little Rock Travelers Go</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {destinations.map((d, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-6 hover:border-[#F6C344] transition-all">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">{d.flag}</span>
                <div>
                  <h3 className="font-bold text-[#1A365D] text-lg">{d.name}</h3>
                  <p className="text-gray-400 text-xs">{d.country}</p>
                </div>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">{d.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection() {
  return (
    <section id="testimonials" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Reviews</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">What Little Rock Travelers Say</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center gap-1 mb-3">
                {[...Array(t.stars)].map((_, j) => <i key={j} className="ri-star-fill text-[#F6C344] text-sm"></i>)}
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 italic">"{t.text}"</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#EEF2F8] flex items-center justify-center">
                  <i className="ri-user-line text-[#1A365D]"></i>
                </div>
                <div>
                  <p className="font-semibold text-[#1A365D] text-sm">{t.name}</p>
                  <p className="text-gray-400 text-xs">{t.city}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AcceptanceFacilitiesSection() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section id="facilities" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Acceptance Facilities</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Acceptance Facilities Near Little Rock</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base">We help you find the facility with the shortest wait time and guide you through the acceptance clerk process.</p>
        </div>
        <div className="space-y-4 max-w-3xl mx-auto">
          {facilities.map((f, i) => (
            <div key={i} className="border border-gray-200 rounded-lg bg-white overflow-hidden">
              <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#EEF2F8]">
                    <i className="ri-map-pin-line text-[#1A365D]"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1A365D] text-base">{f.name}</h3>
                    <p className="text-gray-400 text-xs">{f.address}</p>
                  </div>
                </div>
                <i className={`${open === i ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-gray-400 text-xl`}></i>
              </button>
              {open === i && (
                <div className="px-5 pb-5 pt-0 border-t border-gray-100">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                    <div className="bg-[#F7F8FA] rounded-lg p-3">
                      <p className="text-gray-400 text-xs mb-1">Phone</p>
                      <p className="text-[#1A365D] text-sm font-medium">{f.phone}</p>
                    </div>
                    <div className="bg-[#F7F8FA] rounded-lg p-3">
                      <p className="text-gray-400 text-xs mb-1">Hours</p>
                      <p className="text-[#1A365D] text-sm font-medium">{f.hours}</p>
                    </div>
                    <div className="bg-[#F7F8FA] rounded-lg p-3">
                      <p className="text-gray-400 text-xs mb-1">Wait Time</p>
                      <p className="text-[#1A365D] text-sm font-medium">{f.wait}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ComparisonSection() {
  return (
    <section id="compare" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Compare</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Fast Passport Center vs. DIY Government Processing</h2>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-3 gap-0 border-b border-gray-200 bg-[#EEF2F8]">
            <div className="p-4 font-bold text-[#1A365D] text-sm">Feature</div>
            <div className="p-4 font-bold text-[#1A365D] text-sm text-center">Fast Passport Center</div>
            <div className="p-4 font-bold text-gray-500 text-sm text-center">Government Standard</div>
          </div>
          {comparison.map((row, i) => (
            <div key={i} className={`grid grid-cols-3 gap-0 ${i !== comparison.length - 1 ? "border-b border-gray-100" : ""}`}>
              <div className="p-4 text-sm text-gray-600 font-medium">{row.feature}</div>
              <div className="p-4 text-sm text-[#1A365D] font-semibold text-center bg-[#F7F8FA]/50">{row.us}</div>
              <div className="p-4 text-sm text-gray-500 text-center">{row.them}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function MistakesSection() {
  return (
    <section id="mistakes" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Common Mistakes</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">5 Mistakes Little Rock Applicants Make</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {mistakes.map((m, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-50 mb-4">
                <i className="ri-close-circle-line text-red-500 text-xl"></i>
              </div>
              <h3 className="font-bold text-[#1A365D] text-lg mb-2">{m.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorksSection() {
  const steps = [
    { num: "01", title: "Choose Your Service", desc: "Select emergency, expedited, or standard processing based on your travel timeline." },
    { num: "02", title: "Complete Application", desc: "Fill out the required form (DS-11 or DS-82) with our step-by-step guidance." },
    { num: "03", title: "Visit Acceptance Facility", desc: "We direct you to the nearest Little Rock facility with the shortest wait time." },
    { num: "04", title: "We Handle the Rest", desc: "Your application is hand-carried to the State Department for expedited processing." },
    { num: "05", title: "Receive Your Passport", desc: "Your passport is delivered via secure courier. Track every step in real-time." },
  ];
  return (
    <section id="how-it-works" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">How It Works</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">5 Steps to Your Passport</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          {steps.map((s, i) => (
            <div key={i} className="text-center">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#1A365D] text-white font-bold text-lg mx-auto mb-4">{s.num}</div>
              <h3 className="font-bold text-[#1A365D] text-base mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section id="faq" className="py-16 md:py-24 bg-white">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">FAQ</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Little Rock Passport Questions</h2>
        </div>
        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div key={i} className="border border-gray-200 rounded-lg bg-white overflow-hidden">
              <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left">
                <span className="font-semibold text-[#1A365D] text-sm pr-4">{faq.q}</span>
                <i className={`${open === i ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-gray-400 text-xl flex-shrink-0`}></i>
              </button>
              {open === i && <div className="px-5 pb-5 pt-0 border-t border-gray-100"><p className="text-gray-500 text-sm leading-relaxed">{faq.a}</p></div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HelpfulResourcesSection() {
  return (
    <section id="resources" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Helpful Resources for Little Rock Travelers</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { title: "Travel.State.Gov", desc: "Official U.S. Department of State passport information and form downloads.", link: "https://travel.state.gov/content/travel/en/passports.html" },
            { title: "Little Rock Airport (LIT)", desc: "Flight information and international connections from Clinton National Airport.", link: "https://www.flylittlerock.com/" },
            { title: "CDC Travel Health", desc: "Health recommendations and vaccination requirements for international destinations.", link: "https://wwwnc.cdc.gov/travel" },
          ].map((r, i) => (
            <a key={i} href={r.link} target="_blank" rel="noopener noreferrer" className="bg-white border border-gray-200 rounded-lg p-6 hover:border-[#F6C344] transition-all group">
              <h3 className="font-bold text-[#1A365D] text-lg mb-2 group-hover:text-[#F6C344] transition-colors">{r.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{r.desc}</p>
              <span className="inline-flex items-center gap-1 text-[#1A365D] text-sm font-semibold group-hover:text-[#F6C344] transition-colors">Visit <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform"></i></span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="py-20 md:py-28 bg-[#1A365D]">
      <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
        <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-5">Ready to Get Your Passport in Little Rock?</h2>
        <p className="text-white/70 text-lg max-w-2xl mx-auto mb-8">Don't let a passport delay ruin your travel plans. Whether you need it in 24 hours or 10 days, we've got you covered.</p>
        <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap">
          Start My Passport <i className="ri-arrow-right-line"></i>
        </a>
        <p className="text-white/50 text-sm mt-6">Call us at <a href="tel:8772530084" className="text-[#F6C344] hover:underline">877-253-0084</a> for immediate assistance</p>
      </div>
    </section>
  );
}